/* ============================================================================
   MyPokemonSyncer — drives the My Pokémon cloud backup (2026-08-28).

   Sits inside AuthProvider + SessionProvider (App.tsx, beside WishlistSyncer)
   and never renders. Policy is Backup x Pro, the dex sync's rule:

     - PUSH for every signed-in trainer with the backup toggle on: a debounced
       delta (new/edited records + deletion tombstones, via the push index in
       myPokemonBackup.ts) goes up in chunks after every store change. Backup
       is a safety guarantee, not a paid feature.
     - PULL on sign-in for Pro: the server copy merges into the local store
       record-by-record (LWW on device time, applyMyPokemonBackupPull), which
       is the restore-onto-a-new-phone story. A free trainer's GET returns
       403 pro_required with the backed-up count, surfaced in Settings.

   Every network failure is a logged no-op: the delta stays owed (the index
   only advances on a 2xx) and the next change or sign-in retries it.
============================================================================ */
import { useEffect, useRef } from "react";
import { useAuth } from "@/components/AuthProvider";
import { hasProAccess, useSession } from "@/components/SessionProvider";
import {
  BACKUP_DELETE_CHUNK,
  BACKUP_PUSH_CHUNK,
  MY_POKEMON_BACKUP_NOW_EVENT,
  chunk,
  computeBackupDelta,
  indexAfterPush,
  indexFromPull,
  isMyPokemonBackupEnabled,
  readBackupIndex,
  writeBackupIndex,
  writeBackupMeta,
  type PulledBackupRow,
} from "@/lib/myPokemonBackup";
import {
  applyMyPokemonBackupPull,
  listMyPokemon,
  useMyPokemonRecords,
} from "@/lib/myPokemonStore";

const PUSH_DEBOUNCE_MS = 2_000;

interface PullResponse {
  records?: PulledBackupRow[];
}

export function MyPokemonSyncer() {
  const { isSignedIn, authedFetch } = useAuth();
  const { tier } = useSession();
  const records = useMyPokemonRecords();
  const isPro = hasProAccess(tier);

  const pushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pushInFlightRef = useRef(false);

  /** Push everything the index says the server is still owed. */
  const pushDelta = async (): Promise<void> => {
    if (!isSignedIn || !isMyPokemonBackupEnabled()) return;
    if (pushInFlightRef.current) return; // the debounce re-arms behind it
    pushInFlightRef.current = true;
    try {
      const index = readBackupIndex();
      const delta = computeBackupDelta(listMyPokemon(), index);
      if (delta.changed.length === 0 && delta.tombstones.length === 0) return;
      const recordChunks = chunk(delta.changed, BACKUP_PUSH_CHUNK);
      const deleteChunks = chunk(delta.tombstones, BACKUP_DELETE_CHUNK);
      const batches = Math.max(recordChunks.length, deleteChunks.length);
      for (let i = 0; i < batches; i++) {
        const res = await authedFetch("/api/my-pokemon/backup", {
          method: "PUT",
          body: JSON.stringify({
            records: (recordChunks[i] ?? []).map((r) => ({
              id: r.id,
              payload: r,
              updatedAt: r.updatedAt,
            })),
            deletes: deleteChunks[i] ?? [],
          }),
        });
        // Stop on the first failure: the index has not advanced, so the whole
        // remainder stays owed and retries on the next change.
        if (!res.ok) return;
      }
      const next = indexAfterPush(index, delta);
      writeBackupIndex(next);
      writeBackupMeta({
        lastSyncAt: Date.now(),
        backedUpCount: Object.keys(next).length,
      });
    } catch (err) {
      console.warn("[my-pokemon-backup] push failed", err);
    } finally {
      pushInFlightRef.current = false;
    }
  };

  // Sign-in effect: Pro pulls the server copy down (restore), then pushes
  // what this device holds that the server does not. Free skips straight to
  // the push (backup up-only), noting the pro-locked count for Settings.
  useEffect(() => {
    if (!isSignedIn) return;
    let cancelled = false;
    (async () => {
      if (!isMyPokemonBackupEnabled()) return;
      try {
        const res = await authedFetch("/api/my-pokemon/backup");
        if (cancelled) return;
        if (res.status === 403) {
          const body = (await res.json().catch(() => null)) as { backupCount?: number } | null;
          writeBackupMeta({
            proLockedCount: typeof body?.backupCount === "number" ? body.backupCount : null,
          });
        } else if (res.ok) {
          const data = (await res.json()) as PullResponse;
          const rows = Array.isArray(data.records) ? data.records : [];
          applyMyPokemonBackupPull(rows);
          writeBackupIndex(indexFromPull(rows));
          writeBackupMeta({ lastSyncAt: Date.now(), proLockedCount: null });
        }
        // 404/501 endpoint gap or 5xx: leave local state alone, push anyway —
        // the LWW gate makes a redundant push harmless.
      } catch (err) {
        console.warn("[my-pokemon-backup] pull failed", err);
      }
      if (!cancelled) void pushDelta();
    })();
    return () => {
      cancelled = true;
    };
    // authedFetch is stable; re-run when sign-in state or Pro flips (a fresh
    // upgrade should restore without an app restart).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, isPro]);

  // Change effect: debounce a delta push after every store change.
  useEffect(() => {
    if (!isSignedIn || records.length === 0) {
      // Never push an empty store as a mass deletion on boot: an IDB read
      // race or a fresh install would tombstone the whole backup. Genuine
      // bulk deletes still sync — the store is only ever empty here before
      // first hydration, and single deletes leave the store non-empty.
      return;
    }
    if (pushTimerRef.current) clearTimeout(pushTimerRef.current);
    pushTimerRef.current = setTimeout(() => {
      pushTimerRef.current = null;
      void pushDelta();
    }, PUSH_DEBOUNCE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, isSignedIn]);

  // Settings' "Back up now" button.
  useEffect(() => {
    const handler = () => void pushDelta();
    window.addEventListener(MY_POKEMON_BACKUP_NOW_EVENT, handler);
    return () => window.removeEventListener(MY_POKEMON_BACKUP_NOW_EVENT, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  useEffect(
    () => () => {
      if (pushTimerRef.current) clearTimeout(pushTimerRef.current);
    },
    [],
  );

  return null;
}
